"use client";

import { Suspense, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { apiFetch } from "@/lib/api";

declare global {
  interface Window {
    google?: any;
  }
}

function sanitizeNext(raw: string | null): string {
  if (!raw) return "/";
  let v = raw;
  try {
    for (let i = 0; i < 3; i++) {
      const d = decodeURIComponent(v);
      if (d === v) break;
      v = d;
    }
  } catch {
    /* ignore */
  }
  if (!v.startsWith("/") || v.startsWith("//")) return "/";
  if (v.startsWith("/login") || v.startsWith("/choose-username")) return "/";
  return v;
}

function LoginContent() {
  const search = useSearchParams();
  const next = sanitizeNext(search?.get("next"));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    script.onload = () => {
      const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
      if (!clientId || !window.google) {
        setError("Falta configurar NEXT_PUBLIC_GOOGLE_CLIENT_ID o el script de Google");
        return;
      }
      if (process.env.NODE_ENV !== "production") {
        // Debug origins to catch 403 origin issues.
        console.log("[login] origin", window.location.origin, "clientId", clientId);
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          if (!response?.credential) {
            setError("No se recibió token de Google");
            return;
          }
          try {
            const res = await apiFetch("/api/v1/auth/google", {
              method: "POST",
              body: { id_token: response.credential, next }
            });
            if (res?.status === "needs_username") {
              window.location.replace(res.redirect || "/choose-username");
            } else {
              window.location.replace(res?.redirect || next);
            }
          } catch (e: any) {
            setError(e?.message || "Error al iniciar sesión");
          }
        }
      });
      window.google.accounts.id.renderButton(document.getElementById("google-btn"), {
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "pill"
      });
    };
    return () => {
      document.querySelector('script[src="https://accounts.google.com/gsi/client"]')?.remove();
    };
  }, [next]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="w-full bg-accentStrong text-white text-center py-3 text-lg font-semibold tracking-wide">OFERTASHOY</div>
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md bg-white shadow-md rounded-2xl px-8 py-10 text-center space-y-4">
          <h1 className="text-2xl font-semibold text-gray-900">Bienvenido a OFERTASHOY</h1>
          <p className="text-sm text-gray-600">Ingresá con Google para continuar</p>
          <div className="flex justify-center">
            <div id="google-btn" />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
        </div>
      </main>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="p-6">Cargando login...</div>}>
      <LoginContent />
    </Suspense>
  );
}
